import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Loader2, Flashlight, FlashlightOff } from "lucide-react";
import QrScanner from "qr-scanner";

// Full-screen QR scanner — qr-scanner (native BarcodeDetector where available,
// built-in JS decoder fallback so iOS Safari works too). Camera permission is
// requested ONLY after the user taps Scan; environment camera preferred with an
// enumerated-device fallback chain. The manual property-code entry is ALWAYS
// visible — a camera failure never blocks property access.
export function parsePropertyScan(raw) {
  const text = (raw || "").trim();
  const m = text.match(/\/p\/(\d{4})(?:\/([A-Za-z0-9-]{1,12}))?/i);
  if (m) return { code: m[1], category: m[2] || null };
  if (/^\d{4}$/.test(text)) return { code: text, category: null };
  return null;
}

export default function QRScanModal({ open, onClose }) {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const scannerRef = useRef(null);
  const [status, setStatus] = useState("starting"); // starting | live | error
  const [error, setError] = useState(null);
  const [code, setCode] = useState("");
  const [flash, setFlash] = useState({ has: false, on: false });

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setStatus("starting");
    setError(null);
    setCode("");
    setFlash({ has: false, on: false });
    const fail = (msg) => { if (!cancelled) { setStatus("error"); setError(msg); } };

    (async () => {
      if (!window.isSecureContext) {
        fail("Camera access needs a secure HTTPS connection. Open https://myredpark.com — or enter the property code below.");
        return;
      }
      const video = videoRef.current;
      if (!video) return;
      const onDecode = (r) => {
        const hit = parsePropertyScan(r?.data);
        if (hit) {
          cancelled = true;
          try { scannerRef.current?.destroy(); } catch { /* teardown */ }
          onClose();
          navigate(`/p/${hit.code}${hit.category ? `/${hit.category}` : ""}`);
        }
      };
      try {
        const scanner = new QrScanner(video, onDecode, {
          returnDetailedScanResult: true,
          highlightScanRegion: true,
          highlightCodeOutline: true,
          preferredCamera: "environment",
          maxScansPerSecond: 5,
        });
        scannerRef.current = scanner;
        try {
          await scanner.start(); // rear/environment camera preferred
        } catch (firstErr) {
          // Fallback: permission now granted → enumerate devices, use any camera.
          const cams = await QrScanner.listCameras(true).catch(() => []);
          if (!cams.length) throw firstErr;
          await scanner.setCamera(cams[0].id);
          await scanner.start();
        }
        if (cancelled) return;
        setStatus("live");
        try { if (await scanner.hasFlash()) setFlash({ has: true, on: false }); } catch { /* torch optional */ }
      } catch (e) {
        const name = e?.name || "";
        if (name === "NotAllowedError" || name === "SecurityError") {
          fail("Camera permission was denied. Allow camera access for this site in your browser settings — or enter the property code below.");
        } else if (name === "NotFoundError" || name === "OverconstrainedError") {
          fail("No usable camera was found on this device — enter the property code below.");
        } else if (name === "NotReadableError" || name === "AbortError") {
          fail("The camera is busy in another app. Close it and retry — or enter the property code below.");
        } else {
          fail(`Camera could not start (${name || "browser restriction"}) — enter the property code below.`);
        }
      }
    })();

    return () => {
      cancelled = true;
      try { scannerRef.current?.destroy(); } catch { /* teardown */ }
      scannerRef.current = null;
    };
  }, [open, onClose, navigate]);

  const submitCode = () => {
    const c = code.trim();
    if (!/^\d{4}$/.test(c)) { setError("Enter the 4-digit property code."); return; }
    onClose();
    navigate(`/p/${c}`);
  };

  const toggleTorch = async () => {
    const s = scannerRef.current;
    if (!s) return;
    try {
      if (flash.on) { await s.turnOffFlash(); } else { await s.turnOnFlash(); }
      setFlash((f) => ({ ...f, on: !f.on }));
    } catch { /* torch unsupported mid-stream */ }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[90] bg-black/95 backdrop-blur-sm flex flex-col" data-testid="qr-scan-modal" role="dialog" aria-modal="true" aria-label="Scan MyRedPark QR">
      <div className="h-1 bg-gradient-to-r from-[#E50914] via-[#FF3B45] to-[#E50914] shrink-0" />
      <div className="flex items-center justify-between px-5 py-4 shrink-0">
        <div>
          <p className="meta-label">Scan QR</p>
          <h2 className="font-display font-bold text-lg mt-1">Point at a MyRedPark sign</h2>
        </div>
        <div className="flex items-center gap-2">
          {flash.has && status === "live" && (
            <button onClick={toggleTorch} aria-label={flash.on ? "Turn flashlight off" : "Turn flashlight on"} data-testid="qr-scan-torch"
                    className="p-2.5 rounded-full border border-[#27272A] text-zinc-300 hover:text-white transition-colors">
              {flash.on ? <FlashlightOff className="w-5 h-5" /> : <Flashlight className="w-5 h-5" />}
            </button>
          )}
          <button onClick={onClose} aria-label="Cancel scanning" data-testid="qr-scan-close"
                  className="p-2.5 rounded-full border border-[#27272A] text-zinc-300 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="relative flex-1 min-h-0 mx-4 rounded-2xl overflow-hidden border border-[#27272A] bg-black" data-testid="qr-scan-viewport">
        <video ref={videoRef} className="absolute inset-0 w-full h-full object-cover" muted playsInline aria-label="Camera preview" />
        {/* branded scanning frame */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="relative w-56 h-56">
            {["top-0 left-0 border-t-2 border-l-2 rounded-tl-2xl", "top-0 right-0 border-t-2 border-r-2 rounded-tr-2xl",
              "bottom-0 left-0 border-b-2 border-l-2 rounded-bl-2xl", "bottom-0 right-0 border-b-2 border-r-2 rounded-br-2xl"].map((pos) => (
              <div key={pos} className={`absolute w-10 h-10 border-[#E50914] ${pos}`} />
            ))}
          </div>
        </div>
        {status === "starting" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/60">
            <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
            <p className="text-xs text-zinc-400">Requesting camera access…</p>
          </div>
        )}
        {status === "error" && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/70 p-6">
            <p className="text-sm text-red-300 text-center leading-relaxed" role="alert" data-testid="qr-scan-error">{error}</p>
          </div>
        )}
      </div>

      {/* Manual entry is ALWAYS available — camera failure never blocks access */}
      <div className="shrink-0 p-5 pb-8 max-w-md w-full mx-auto">
        <p className="text-xs text-zinc-400 mb-2.5">
          {status === "error" ? "Camera unavailable? Enter Property Code" : "Have the 4-digit code instead?"}
        </p>
        <div className="flex gap-2.5">
          <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 4))}
                 onKeyDown={(e) => { if (e.key === "Enter") submitCode(); }}
                 inputMode="numeric" autoComplete="off" placeholder="4821" aria-label="Property code"
                 data-testid="qr-scan-code-input"
                 className="flex-1 min-w-0 rounded-xl border border-[#27272A] bg-[#0A0D10] px-4 py-3 text-base text-white font-mono tracking-[0.3em] text-center focus:outline-none focus:ring-2 focus:ring-[#E50914]" />
          <button onClick={submitCode} data-testid="qr-scan-code-go" className="btn-primary !px-6 text-sm">Continue</button>
        </div>
        <p className="mt-3 text-[11px] text-zinc-600 text-center">Video never leaves your device — only the scanned code is sent to the server.</p>
      </div>
    </div>
  );
}
